// src/components/payments/PaymentRequestDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  DollarSign,
  FileText,
  Banknote,
  TrendingUp,
  QrCode,
  Package,
  Eye,
  Filter,
  Search,
  BarChart3,
  Trash2,
  AlertTriangle,
  Download
} from 'lucide-react';
import { GET_TRANSACTIONS_BY_USER } from '../../api/transactions-api';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { exportToExcel } from '../../utils/exportToExcel';

// Add the DELETE_DUPLICATE_TRANSACTION mutation
const DELETE_DUPLICATE_TRANSACTION = gql`
  mutation DeleteDuplicateTransaction($transactionId: ID!) {
    deleteDuplicateTransaction(transactionId: $transactionId) {
      success
      message
      transactionId
      userAmountSubtracted
      adminAmountSubtracted
    }
  }
`;

const PaymentRequestDetailsModal = ({ request, isOpen, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [transactionFilters, setTransactionFilters] = useState({
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    type: ''
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Fetch user's transactions
  const { 
    loading: transactionsLoading, 
    error: transactionsError, 
    data: transactionsData,
    refetch: refetchTransactions
  } = useQuery(GET_TRANSACTIONS_BY_USER, {
    variables: { userId: request?.userId },
    skip: !request?.userId || activeTab !== 'qr-scans',
    fetchPolicy: 'cache-and-network'
  });

  // Delete duplicate transaction mutation
  const [deleteDuplicateTransaction, { loading: deleting }] = useMutation(DELETE_DUPLICATE_TRANSACTION, {
    onCompleted: (data) => {
      if (data.deleteDuplicateTransaction.success) {
        toast.success(data.deleteDuplicateTransaction.message);
        refetchTransactions();
        if (onRefresh) onRefresh();
        setShowDeleteConfirmation(false);
        setTransactionToDelete(null);
      } else {
        toast.error(data.deleteDuplicateTransaction.message);
      }
    },
    onError: (error) => {
      console.error('Error deleting duplicate transaction:', error);
      toast.error('Erreur lors de la suppression de la transaction dupliquée');
    }
  });

  const userTransactions = transactionsData?.transactionsByUser || [];

  if (!isOpen || !request) return null;

  const getStatusStyle = (status) => {
    switch (status) {
      case 'VALIDATED':
        return { 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-800',
          icon: <CheckCircle size={16} className="mr-2 text-green-600" /> 
        };
      case 'PENDING':
        return { 
          bgColor: 'bg-yellow-100', 
          textColor: 'text-yellow-800',
          icon: <Clock size={16} className="mr-2 text-yellow-600" /> 
        };
      case 'REJECTED':
        return { 
          bgColor: 'bg-red-100', 
          textColor: 'text-red-800',
          icon: <XCircle size={16} className="mr-2 text-red-600" /> 
        };
      default:
        return { 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-800',
          icon: <AlertCircle size={16} className="mr-2 text-gray-600" /> 
        };
    }
  };

  const getUserTypeLabel = (userType) => {
    switch(userType) {
      case 'PHARMACIST': return 'Pharmacien';
      case 'SELLER': return 'Vendeur';
      case 'LABORATORY': return 'Laboratoire';
      case 'ADMIN': return 'Admin';
      default: return userType;
    }
  };

  const statusStyle = getStatusStyle(request.status);

  // Filter transactions based on search criteria
  const filteredTransactions = userTransactions.filter(transaction => {
    // FIRST: Only show transactions related to this payment request
    // If the transaction has a paymentRequestId, it must match the current request
    if (transaction.paymentRequestId && transaction.paymentRequestId !== request.id) {
      return false;
    }
    
    // For transactions without paymentRequestId, you might want to include or exclude them
    // depending on your business logic. If you want to show ONLY transactions
    // that are specifically linked to this payment request, uncomment the line below:
    // if (!transaction.paymentRequestId) return false;
  
    // Search term filter
    if (transactionFilters.searchTerm) {
      const term = transactionFilters.searchTerm.toLowerCase();
      const searchableText = [
        transaction.label?.code || '',
        transaction.product?.name || '',
        transaction.label?.promotion?.name || '',
        transaction.pharmacy?.namePharmacy || ''
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(term)) return false;
    }
  
    // Date range filter
    if (transactionFilters.dateFrom && transaction.date) {
      const transactionDate = new Date(transaction.date);
      const fromDate = new Date(transactionFilters.dateFrom);
      if (transactionDate < fromDate) return false;
    }
  
    if (transactionFilters.dateTo && transaction.date) {
      const transactionDate = new Date(transaction.date);
      const toDate = new Date(transactionFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (transactionDate > toDate) return false;
    }
  
    // Status filter
    if (transactionFilters.status && transaction.status !== transactionFilters.status) return false;
  
    // Type filter
    if (transactionFilters.type && transaction.type !== transactionFilters.type) return false;
  
    return true;
  });

  // SCAN transactions for this payment request:
  // - exclude transactions linked to a DIFFERENT payment request
  // - exclude non-SCAN types (withdrawals, bonuses)
  // - apply search/date/status filters
  const scanTransactions = userTransactions.filter(t => {
    if (t.type !== 'SCAN') return false;
    if (t.paymentRequestId !== request.id) return false;
    if (transactionFilters.searchTerm) {
      const term = transactionFilters.searchTerm.toLowerCase();
      const txt = [t.label?.code || '', t.product?.name || '', t.label?.promotion?.name || '', t.pharmacy?.namePharmacy || ''].join(' ').toLowerCase();
      if (!txt.includes(term)) return false;
    }
    if (transactionFilters.dateFrom && t.date && new Date(t.date) < new Date(transactionFilters.dateFrom)) return false;
    if (transactionFilters.dateTo && t.date) {
      const to = new Date(transactionFilters.dateTo); to.setHours(23, 59, 59, 999);
      if (new Date(t.date) > to) return false;
    }
    if (transactionFilters.status && t.status !== transactionFilters.status) return false;
    return true;
  });

  // Count how many times each QR code appears in the current scan list
  const qrCodeCounts = scanTransactions.reduce((acc, t) => {
    const code = t.label?.code;
    if (code) acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  // 3. Stats always mirror the visible table (scanTransactions)
  const transactionStats = {
    total: scanTransactions.length,
    scans: scanTransactions.length,
    withdrawals: userTransactions.filter(t => t.type === 'WITHDRAWAL').length,
    validated: scanTransactions.filter(t => t.status === 'VALIDATED').length,
    pending: scanTransactions.filter(t => t.status === 'PENDING').length,
    totalEarnings: scanTransactions.reduce((sum, t) => sum + (t.gain || 0), 0),
    totalCommission: scanTransactions.reduce((sum, t) => sum + (t.companyCommission || 0), 0),
    thisMonth: scanTransactions.filter(t => {
      if (!t.date) return false;
      const d = new Date(t.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length,
    thisMonthEarnings: scanTransactions.filter(t => {
      if (!t.date) return false;
      const d = new Date(t.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).reduce((sum, t) => sum + (t.gain || 0), 0)
  };

  const hasDuplicateQRCode = (transaction) => {
    return transaction.type === 'SCAN'
      && !!transaction.label?.code
      && (qrCodeCounts[transaction.label.code] || 0) > 1;
  };

  const exportScansToExcel = () => {
    const headers = ['Code QR', 'Produit', 'Gain (DH)', 'Commission Mazid (DH)', 'Date', 'Statut', 'Pharmacie', 'Région'];
    const rows = scanTransactions.map(t => [
      t.label?.code || '',
      t.product?.name || '',
      t.gain != null ? t.gain.toFixed(2) : '0.00',
      t.companyCommission != null ? t.companyCommission.toFixed(2) : '0.00',
      t.date ? format(new Date(t.date), 'dd/MM/yyyy HH:mm') : '',
      t.status || '',
      t.pharmacy?.namePharmacy || '',
      t.pharmacy?.region?.nomRegion || '',
    ]);
    exportToExcel(headers, rows, `qr_scans_${request.id.slice(0, 8)}.xls`);
  };

  const handleTransactionFilterChange = (e) => {
    const { name, value } = e.target;
    setTransactionFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearTransactionFilters = () => {
    setTransactionFilters({
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      status: '',
      type: ''
    });
  };

  // Handle remove duplicate transaction
  const handleRemoveDuplicate = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteConfirmation(true);
  };

  const confirmRemoveDuplicate = async () => {
    if (!transactionToDelete) return;
    
    try {
      await deleteDuplicateTransaction({
        variables: { transactionId: transactionToDelete.id }
      });
    } catch (error) {
      console.error('Error removing duplicate transaction:', error);
    }
  };

  const cancelRemoveDuplicate = () => {
    setShowDeleteConfirmation(false);
    setTransactionToDelete(null);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Overlay — click outside closes */}
        <div className="fixed inset-0 bg-black bg-opacity-60" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Détails de la demande de paiement</h2>
            <p className="text-sm text-gray-500 mt-1">
              {request.user ? `${request.user.firstName} ${request.user.lastName}` : 'Utilisateur non spécifié'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Tabs */}
          <div className="border-b">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: <FileText size={16} /> },
                { id: 'qr-scans', label: 'Scans QR', icon: <QrCode size={16} /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Duplicate warning banner */}
                {Object.values(qrCodeCounts).some(c => c > 1) && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">Transactions dupliquées détectées</p>
                      <p className="text-xs text-red-600 mt-0.5">
                        Cette demande contient des scans QR identiques effectués plusieurs fois.
                        Vérifiez l'onglet <span className="font-semibold">Scans QR</span> pour identifier et supprimer les doublons avant validation.
                      </p>
                    </div>
                  </div>
                )}
                {/* Request Status and Info */}
                <div className="bg-white border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Informations de la demande</h3>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bgColor} ${statusStyle.textColor}`}>
                      {statusStyle.icon}
                      {request.status}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Montant demandé</label>
                        <p className="text-lg font-semibold text-gray-900">{request.amount?.toFixed(2)} DH</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Date de demande</label>
                        <p className="text-sm text-gray-900">
                          {format(new Date(request.dateDemande), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                      {request.rejectionReason && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Raison du refus</label>
                          <p className="text-sm text-red-600">{request.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      {request.user && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Utilisateur</label>
                          <div className="flex items-center space-x-2 mt-1">
                            <User size={16} className="text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {request.user.firstName} {request.user.lastName}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <Mail size={16} className="text-gray-400" />
                            <span className="text-sm text-gray-600">{request.user.email}</span>
                          </div>
                          {request.user.tel && (
                            <div className="flex items-center space-x-2 mt-1">
                              <Phone size={16} className="text-gray-400" />
                              <span className="text-sm text-gray-600">{request.user.tel}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* User Balance Information */}
                {request.user && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Solde de l'utilisateur</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-emerald-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <DollarSign size={20} className="text-emerald-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-emerald-800">Solde commission</p>
                            <p className="text-lg font-semibold text-emerald-900">
                              {request.user.commissionBalance?.toFixed(2) || '0.00'} DH
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircle size={20} className="text-blue-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">Commission approuvée</p>
                            <p className="text-lg font-semibold text-blue-900">
                              {request.user.approvedCommission?.toFixed(2) || '0.00'} DH
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <Clock size={20} className="text-yellow-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-yellow-800">Commission en attente</p>
                            <p className="text-lg font-semibold text-yellow-900">
                              {request.user.waitingCommission?.toFixed(2) || '0.00'} DH
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'qr-scans' && (
              <div className="space-y-6">
                {/* Export button */}
                {scanTransactions.length > 0 && (
                  <div className="flex justify-end">
                    <button
                      onClick={exportScansToExcel}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                    >
                      <Download size={15} /> Exporter Excel ({scanTransactions.length} codes)
                    </button>
                  </div>
                )}
                {/* Transaction Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <TrendingUp size={20} className="text-emerald-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Total des gains</p>
                        <p className="text-lg font-semibold text-emerald-900">
                          {transactionStats.totalEarnings.toFixed(2)} DH
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <QrCode size={20} className="text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Total scans QR</p>
                        <p className="text-lg font-semibold text-blue-900">{scanTransactions.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle size={20} className="text-purple-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-purple-800">Validées</p>
                        <p className="text-lg font-semibold text-purple-900">{transactionStats.validated}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Clock size={20} className="text-orange-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-orange-800">En attente</p>
                        <p className="text-lg font-semibold text-orange-900">{transactionStats.pending}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transaction Filters */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-64">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rechercher
                      </label>
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="searchTerm"
                          value={transactionFilters.searchTerm}
                          onChange={handleTransactionFilterChange}
                          placeholder="Code QR, produit, pharmacie..."
                          className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Du
                      </label>
                      <input
                        type="date"
                        name="dateFrom"
                        value={transactionFilters.dateFrom}
                        onChange={handleTransactionFilterChange}
                        className="rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Au
                      </label>
                      <input
                        type="date"
                        name="dateTo"
                        value={transactionFilters.dateTo}
                        onChange={handleTransactionFilterChange}
                        className="rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Statut
                      </label>
                      <select
                        name="status"
                        value={transactionFilters.status}
                        onChange={handleTransactionFilterChange}
                        className="rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      >
                        <option value="">Tous les statuts</option>
                        <option value="PENDING">En attente</option>
                        <option value="VALIDATED">Validé</option>
                        <option value="REJECTED">Rejeté</option>
                      </select>
                    </div>
                    
                    <button
                      onClick={clearTransactionFilters}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <Filter size={16} className="mr-2" />
                      Effacer
                    </button>
                  </div>
                </div>

                {/* Transactions Table */}
                {transactionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
                    <span className="ml-2 text-gray-500">Chargement des transactions...</span>
                  </div>
                ) : scanTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun scan QR trouvé</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {Object.values(transactionFilters).some(f => f) ?
                        "Aucun scan ne correspond à vos critères." :
                        "Cet utilisateur n'a encore effectué aucun scan QR."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transaction
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Produit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Code QR
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Gain
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Statut
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pharmacie
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {scanTransactions.map((transaction) => {
                          const isDuplicate = hasDuplicateQRCode(transaction);
                          return (
                            <tr 
                              key={transaction.id} 
                              className={`${
                                isDuplicate 
                                  ? 'bg-red-50 border-l-4 border-red-400' 
                                  : ''
                              }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8">
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                      transaction.type === 'SCAN' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                                    }`}>
                                      {transaction.type === 'SCAN' ? <QrCode size={16} /> : <Package size={16} />}
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {transaction.type === 'SCAN' ? 'Scan QR' : 'Retrait'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      ID: {transaction.id.slice(0, 8)}...
                                    </div>
                                    {isDuplicate && (
                                      <div className="flex items-center text-xs text-red-600 mt-1">
                                        <AlertTriangle size={12} className="mr-1" />
                                        Transaction dupliquée
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {transaction.product ? (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {transaction.product.name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {transaction.product.price?.toFixed(2)} DH
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">Produit non défini</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {transaction.label?.code ? (
                                  <div>
                                    <div className="text-sm font-mono text-gray-900">
                                      {transaction.label.code}
                                    </div>
                                    {isDuplicate && (
                                      <div className="text-xs text-red-600">
                                        {qrCodeCounts[transaction.label.code]} utilisations
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">Code non défini</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {transaction.gain?.toFixed(2)} DH
                                </div>
                                <div className="text-xs text-gray-500">
                                  Commission Mazid: {transaction.companyCommission?.toFixed(2)} DH
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {transaction.date ? format(new Date(transaction.date), 'dd/MM/yyyy HH:mm') : 'Date non définie'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  transaction.status === 'VALIDATED' 
                                    ? 'bg-green-100 text-green-800'
                                    : transaction.status === 'PENDING'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {transaction.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {transaction.pharmacy ? (
                                  <div className="flex items-center">
                                    <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                                    <div>
                                      <div className="text-sm text-gray-900">
                                        {transaction.pharmacy.namePharmacy}
                                      </div>
                                      {transaction.pharmacy.region && (
                                        <div className="text-xs text-gray-500">
                                          {transaction.pharmacy.region.nomRegion}
                                        </div>
                                      )}
                                      {transaction.pharmacy.ville && (
                                        <div className="text-xs text-gray-500">
                                          {transaction.pharmacy.ville.nomVilleVillage}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">Pharmacie non définie</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {isDuplicate && (
                                  <button
                                    onClick={() => handleRemoveDuplicate(transaction)}
                                    disabled={deleting}
                                    className="inline-flex items-center px-3 py-1 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Supprimer cette transaction dupliquée"
                                  >
                                    <Trash2 size={12} className="mr-1" />
                                    {deleting ? 'Suppression...' : 'Supprimer'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-6 border-t pt-4 flex justify-between text-xs text-gray-500 px-6">
          <span>Créé le: {format(new Date(request.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
          <span>Modifié le: {format(new Date(request.updatedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end space-x-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && transactionToDelete && (
        <div className="fixed inset-0 z-60 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Supprimer la transaction dupliquée
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Êtes-vous sûr de vouloir supprimer cette transaction dupliquée ? Cette action :
                    </p>
                    <ul className="mt-2 text-sm text-gray-500 list-disc list-inside space-y-1">
                      <li>Supprimera définitivement la transaction</li>
                      <li>Réduira le montant de la demande de paiement de <strong>{transactionToDelete.gain?.toFixed(2)} DH</strong></li>
                      <li>La cagnotte de l'utilisateur ne sera <strong>pas modifiée</strong></li>
                      <li>Cette action est irréversible</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400">
                <div className="text-sm text-yellow-700">
                  <p className="font-medium">Détails de la transaction :</p>
                  <ul className="mt-1 space-y-1">
                    <li>Code QR: <span className="font-mono">{transactionToDelete.label?.code}</span></li>
                    <li>Produit: {transactionToDelete.product?.name}</li>
                    <li>Date: {transactionToDelete.date ? format(new Date(transactionToDelete.date), 'dd/MM/yyyy HH:mm') : 'Non définie'}</li>
                    <li>Pharmacie: {transactionToDelete.pharmacy?.namePharmacy || 'Non définie'}</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
              <button
                onClick={cancelRemoveDuplicate}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={confirmRemoveDuplicate}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Suppression...
                  </div>
                ) : (
                  'Supprimer définitivement'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PaymentRequestDetailsModal;